import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  nextEditingFieldKey,
  type TaskPencilFieldKey,
  type TaskPersistOverrides,
} from './taskFieldEditModel';

type TaskFieldEditContextValue = {
  /** When true, selects/sections/pencil commits write a new tip version. */
  autoPersist: boolean;
  persistTip: (overrides?: TaskPersistOverrides) => void | Promise<void>;
  editingFieldKey: TaskPencilFieldKey | null;
  beginEdit: (key: TaskPencilFieldKey) => void;
  endEdit: () => void;
};

const TaskFieldEditContext = createContext<TaskFieldEditContextValue | null>(
  null
);

export function TaskFieldEditProvider({
  autoPersist,
  persistTip,
  children,
}: {
  autoPersist: boolean;
  persistTip: (overrides?: TaskPersistOverrides) => void | Promise<void>;
  children: React.ReactNode;
}) {
  const [editingFieldKey, setEditingFieldKey] =
    useState<TaskPencilFieldKey | null>(null);

  const beginEdit = useCallback((key: TaskPencilFieldKey) => {
    setEditingFieldKey((current) => nextEditingFieldKey(current, key));
  }, []);

  const endEdit = useCallback(() => {
    setEditingFieldKey(null);
  }, []);

  const value = useMemo(
    () => ({
      autoPersist,
      persistTip,
      editingFieldKey,
      beginEdit,
      endEdit,
    }),
    [autoPersist, beginEdit, editingFieldKey, endEdit, persistTip]
  );

  return (
    <TaskFieldEditContext.Provider value={value}>
      {children}
    </TaskFieldEditContext.Provider>
  );
}

const NOOP_PERSIST = async () => undefined;

const FALLBACK: TaskFieldEditContextValue = {
  autoPersist: false,
  persistTip: NOOP_PERSIST,
  editingFieldKey: null,
  beginEdit: () => undefined,
  endEdit: () => undefined,
};

export function useTaskFieldEdit(): TaskFieldEditContextValue {
  return useContext(TaskFieldEditContext) ?? FALLBACK;
}
